window.loadedSentryIntegrations = window.loadedSentryIntegrations || [];

var supportedChromeMilestones = null;

// NOTE: only fetch Chrome versions if the current browser is a Chrome variant
var isChrome = (/Chrome\//i.test(navigator.userAgent) && !/Edg\//i.test(navigator.userAgent));
if (isChrome) {
  (function fetchSupportedChromeVersions() {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', 'https://cdn.jsdelivr.net/gh/berstend/chrome-versions/data/stable/all/version/list.json', true);
    xhr.timeout = 2000; // NOTE: only wait 2 seconds for Chrome versions data
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4 && xhr.status === 200) {
        try {
          var data = JSON.parse(xhr.responseText);
          var milestones = {};
          var keys = Object.keys(data);
          for (var i = 0; i < keys.length; i++) {
            var platform = data[keys[i]];
            if (Array.isArray(platform)) {
              for (var j = 0; j < platform.length; j++) {
                var entry = platform[j];
                if (entry.milestone) {
                  milestones[entry.milestone] = true;
                }
              }
            }
          }
          supportedChromeMilestones = milestones;
        } catch (err) {
          console.error('Failed to parse Chrome versions for Sentry filtering:', err);
        }
      }
    };
    xhr.onerror = function() {
      console.error('Failed to fetch supported Chrome versions for Sentry filtering');
    };
    xhr.ontimeout = function() {
      console.error('Timeout fetching supported Chrome versions for Sentry filtering');
    };
    xhr.send();
  })();
}


function filterAlreadyLoadedIntegrations(wantedIntegrations) {
  const result = [];
  for (const integration of (wantedIntegrations || [])) {
	if (!window.loadedSentryIntegrations.includes(integration.name)) {
	  window.loadedSentryIntegrations.push(integration.name);
	  result.push(integration);
	}
  }
  return result;
}


export default function getSentryConfig(Sentry) {
  const wantedIntegrations = [
	Sentry.browserTracingIntegration(),
	Sentry.extraErrorDataIntegration(),
	// Sentry.replayIntegration(),
  ];
  const dropUrls = [
    /recaptcha/i,
    /crazyegg\.com/i,
    /script\.crazyegg\.com/i,
    /\/pages\/versioned\/.*scripts\//i,  // NOTE: CrazyEgg's path pattern (domain may be stripped if cross-domain)
    /freshchat/i,
    /bglinks/i,
    /widget\.js/i,
	/translate\.goog/i,
  ];
  const dropModules = [
	/^plyr/i,
	/^recaptcha/i,
  ];
  const config = {
	dsn: window.SENTRY_DSN,
	attachStacktrace: true,
	normalizeDepth: 8,
	environment: window.ENVIRONMENT,
	allowUrls: [
      new RegExp(`${window.location.origin}/`, "i"),
      new RegExp(`${window.STATIC_URL}`, "i"),
	],
	ignoreErrors: [
	  "Invalid site key or not loaded",
	  /^recaptcha/i,
	  /Can't find variable: gmo$/i,
	  "Java exception was raised during method invocation",
	  /Java object is gone$/i,
	  /Non-Error promise rejection captured/,
	  "Object Not Found Matching Id",
      "ResizeObserver loop limit exceeded",
	  /window\.webkit\.messageHandlers/i,
	  /window.__firefox__.reader/,
	  /evaluating 'window.webkit.messageHandlers'/,
	  // Timeout errors from third-party libraries (e.g., QR scanner, bglinks)
	  /timeout/i,
	  /^Timeout$/i,
	  /Scanner error: timeout/i,
	],
	tracesSampleRate: window.SENTRY_TRACE_SAMPLE_RATE || 1.0,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: window.SENTRY_SAMPLE_RATE,
	beforeSend: function(event) {
	  const isProd = window.ENVIRONMENT == "production";
	  const validRelease = /^[\d\.]+$/.test(window.SITE_SENTRY_RELEASE || "");
	  // Drop events from cross-origin requests
	  if (event && event.request && event.request.url) {
		var requestUrl = event.request.url;
		if (typeof requestUrl === "string" && requestUrl.length > 0) {
		  try {
			var requestOrigin = new URL(requestUrl).origin;
			if (requestOrigin !== window.location.origin) {
			  return null;
			}
		  } catch (e) {
			// Invalid URL, allow event through
		  }
		}
	  }
	  if (event.message?.includes("performanceMetrics")) {
		console.debug("Dropping error involving performance metrics", window.SITE_SENTRY_RELEASE);
		return null;
	  }
	  if (isProd && !validRelease) {
		console.debug("Dropping error from invalid release", window.SITE_SENTRY_RELEASE);
		return null;
	  }
	  try {
		// Filter out old Chrome versions not in the latest supported milestones
		if (supportedChromeMilestones !== null) {
		  var browserCtx = (event && event.contexts && event.contexts.browser) || {};
		  var browserName = browserCtx.name || '';
		  var browserVersion = browserCtx.version || '';
		  // Fallback to navigator.userAgent if Sentry event doesn't have browser context
		  if (!browserName || !browserVersion) {
			var uaMatch = navigator.userAgent.match(/Chrome\/(\d+(?:\.\d+)*)/i);
			if (uaMatch) {
			  browserName = 'Chrome';
			  browserVersion = uaMatch[1];
			}
		  }
		  // Check for Chrome variants (Chrome, Chrome Mobile, Chrome Headless, etc.)
		  if (/^Chrome/i.test(browserName) && browserVersion) {
			var majorVersion = parseInt(browserVersion.split('.')[0], 10);
			if (!isNaN(majorVersion) && !supportedChromeMilestones[majorVersion]) {
			  console.debug("Ignoring error from old Chrome version", browserVersion);
			  return null;
			}
		  }
		}

		var values = (event && event.exception && event.exception.values) || [];
		var frames = (values[0] && values[0].stacktrace && values[0].stacktrace.frames) || [];
		if (frames.length) {
		  var firstFrame = frames[0];
		  var absPath = firstFrame.abs_path;
		  if (absPath && typeof absPath === "string") {
			try {
			  var frameUrl = new URL(absPath);
			  // Full URL - check if origin is ours or static CDN
			  var pageOrigin = window.location.origin;
			  var staticOrigin;
			  try {
				staticOrigin = new URL(window.STATIC_URL || "", window.location.origin).origin;
			  } catch (e) {
				staticOrigin = pageOrigin;
			  }
			  if (frameUrl.origin !== pageOrigin && frameUrl.origin !== staticOrigin) {
				return null;
			  }
			} catch (e) {
			  // Not a full URL - just a path (e.g. /static/js/app.js), allow
			}
		  }
		  // Check first frame (error origin) against known 3rd-party scripts - drop those, keep ours
		  var urlToCheck = firstFrame.abs_path || firstFrame.filename || '';
		  if (dropUrls.some(function(pattern) { return pattern.test(urlToCheck); })) {
			return null;
		  }
		  if (dropModules.some(function(pattern) { return pattern.test(firstFrame.module); })) {
			return null;
		  }
		}
	  } catch (err) {
		console.error("Error in beforeSend filter", err);
	  }
	  return event;
	}
  };
  if (window.SITE_SENTRY_RELEASE) {
	config.release = window.SITE_SENTRY_RELEASE;
  }
  return config;
}
